/**
 * Top "single short sentence stating the observed outcome" — see Slice 3
 * → "Top". Selection priority order is preserved verbatim from the PRD.
 */

import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';
import {
  lookupConnectionDiagnosticsError,
  type DiagnosticsErrorMessage,
} from '../connectionDiagnosticsErrorMap';

type HeadlineProps = {
  report: DiagnosticsReport;
  /** From Slice 1's expanded error shape — true when the listing query timed out. */
  timedOut?: boolean;
  /** From Slice 1 — true when listing succeeded but returned zero rows. */
  listingSucceededEmpty?: boolean;
  /** Driver-level code (e.g. `ELOGIN`) lifted from Slice 1's error shape. */
  driverCode?: string;
};

type Headline = {
  sentence: string;
  mappedDescription?: string;
};

function buildHeadline(props: HeadlineProps): Headline {
  const { report, timedOut, listingSucceededEmpty, driverCode } = props;

  // Slice 11 — NO_POOL gets a specific, actionable headline + Reconnect
  // button (the button itself lives in the dialog footer). The wording is
  // load-bearing for the E2E spec ("connection to the server is closed").
  if (report.databases.listingErrorReason === 'no_pool') {
    return {
      sentence:
        'Listing your databases failed because the connection to the server is closed. Click Reconnect below to reopen it.',
    };
  }

  if (timedOut) {
    return {
      sentence: 'Listing your databases took longer than 15 seconds and was aborted.',
    };
  }

  if (report.triggeredBy === 'pool_open_failure') {
    const code = report.scopeError?.sqlErrorCode;
    const mapped = lookupConnectionDiagnosticsError({
      sqlErrorCode: code,
      code: driverCode,
      fallbackMessage: report.scopeError?.message,
    });
    const codeLabel = code !== undefined ? `SQL error ${code}` : 'a server error';
    const dbLabel = report.scopeDatabase ?? 'this database';
    return {
      sentence: `We couldn't open ${dbLabel} (${codeLabel}).`,
      mappedDescription: mapped.description,
    };
  }

  const listingErr = report.databases.listingError;
  if (listingErr?.sqlErrorCode !== undefined) {
    const mapped = lookupConnectionDiagnosticsError({
      sqlErrorCode: listingErr.sqlErrorCode,
      code: driverCode,
      fallbackMessage: listingErr.message,
    });
    return {
      sentence: `Listing your databases failed with SQL error ${listingErr.sqlErrorCode}.`,
      mappedDescription: mapped.description,
    };
  }

  if (listingErr && driverCode) {
    const mapped = lookupConnectionDiagnosticsError({
      code: driverCode,
      fallbackMessage: listingErr.message,
    });
    return {
      sentence: 'Listing your databases failed with a server error.',
      mappedDescription: mapped.description,
    };
  }

  if (listingSucceededEmpty) {
    return { sentence: 'The server returned no databases for your account.' };
  }

  return { sentence: "We couldn't list databases on this server." };
}

export function DiagnosticsHeadline(props: HeadlineProps) {
  const { sentence, mappedDescription } = buildHeadline(props);
  return (
    <div data-testid="diag-headline" className="space-y-1">
      <p className="text-sm font-medium">{sentence}</p>
      {mappedDescription && (
        <p className="text-sm text-muted-foreground">{mappedDescription}</p>
      )}
    </div>
  );
}

export type { DiagnosticsErrorMessage };
